import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader, getRequestIP } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";
import { formatDesignAnswer, parseDesignSnapshot } from "./campaign-design-answer";
import {
  FIELD_TYPE_LABELS,
  OTHER_VALUE,
  applyPlaceholders,
  formatEuroAmount,
  computeProgress,
  isAnswered,
  isQuestionVisible,
  matrixRows,
  matrixSteps,
  parseConfig,
  type AnswerLike,
  type QConfig,
  type QCondition,
  type QuestionLike,
} from "@/lib/questionnaire-logic";


type Ctx = { supabase: any; userId: string };

type QTemplate = Database["public"]["Tables"]["questionnaire_templates"]["Row"];
type QQuestion = Database["public"]["Tables"]["questionnaire_questions"]["Row"];
type QResponse = Database["public"]["Tables"]["questionnaire_responses"]["Row"];
type QAnswer = Database["public"]["Tables"]["questionnaire_answers"]["Row"];
type QFieldType = Database["public"]["Enums"]["questionnaire_field_type"];
type QStatus = Database["public"]["Enums"]["questionnaire_status"];

async function assertStaff(ctx: Ctx): Promise<"admin" | "editor"> {
  const [{ data: isAdmin }, { data: isEditor }] = await Promise.all([
    ctx.supabase.rpc("has_role", { _user_id: ctx.userId, _role: "admin" }),
    ctx.supabase.rpc("has_role", { _user_id: ctx.userId, _role: "editor" }),
  ]);
  if (isAdmin) return "admin";
  if (isEditor) return "editor";
  throw new Error("Forbidden");
}

async function logAccess(
  ctx: Ctx,
  action:
    | "QUESTIONNAIRE_VIEW"
    | "QUESTIONNAIRE_EXPORT_PDF"
    | "QUESTIONNAIRE_EXPORT_CSV"
    | "QUESTIONNAIRE_FILE_ACCESS",
  responseId: string,
  meta: Record<string, unknown>,
) {
  try {
    let ip: string | null = null;
    let ua: string | null = null;
    try {
      ip = getRequestIP({ xForwardedFor: true }) ?? null;
      ua = getRequestHeader("user-agent") ?? null;
    } catch {
      /* nicht in Request-Kontext */
    }
    await (await import("@/lib/audit.server")).writeAuditEvent({
      action,
      tableName: "questionnaire_responses",
      recordId: responseId,
      newData: meta,
      context: "admin_questionnaire",
      ip,
      userAgent: ua,
      actorId: ctx.userId,
    });
  } catch (err) {
    console.error("[audit] questionnaire access log failed", err);
  }
}

const ANSWER_COLUMNS =
  "id, question_id, value_text, value_bool, value_date, value_selected, file_path, value_files, value_number, value_matrix, value_panels, value_other_text, value_unknown, updated_at";


// ============================================================
// ÜBERSICHT
// ============================================================

export type OverviewRow = {
  responseId: string | null;
  assignmentId: string;
  clientId: string;
  clientName: string;
  clientNumber: string | null;
  templateId: string;
  templateTitle: string;
  templateType: QTemplate["type"];
  templateVariant: QTemplate["variant"];
  status: QStatus;
  lastActivity: string | null;
  completedAt: string | null;
  startedAt: string | null;
  answeredRequired: number;
  totalRequired: number;
  answeredQuestions: number;
  totalQuestions: number;
  /** 0..1 – Ausfüllgrad über alle sichtbaren Fragen (Pflicht wie optional). */
  progress: number;
  /** Ticket AD4a: Zeitpunkt der Nachbearbeitung, `null` = noch offen. */
  reviewedAt: string | null;
  /**
   * Interner Archivstand der Zuweisung. `null` = aktiv. Nur der interne
   * Bereich filtert danach; das Kundencockpit ignoriert das Feld bewusst.
   */
  archivedAt: string | null;
};

export const listQuestionnaireOverview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        clientId: z.string().uuid().nullable().optional(),
        type: z
          .enum(["personaldatenblatt", "wahlkampf", "ttpa", "design", "design_standard", "test"])
          .nullable()
          .optional(),
        status: z
          .enum(["not_started", "in_progress", "completed"])
          .nullable()
          .optional(),
        search: z.string().max(200).nullable().optional(),
        /** Standard: nur aktive (nicht archivierte) Einträge. */
        archived: z.enum(["active", "archived", "all"]).optional(),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ context, data }): Promise<{ rows: OverviewRow[] }> => {
    await assertStaff(context);

    const archivedView = data.archived ?? "active";

    // Alle Zuweisungen + Templates + Kundendaten
    let assignQuery = context.supabase
      .from("client_questionnaire_assignments")
      .select(
        "id, client_id, template_id, archived_at, clients:client_id(id, organization_name, moco_client_number), questionnaire_templates:template_id(id, title, type, variant, published)",
      );
    if (data.clientId) assignQuery = assignQuery.eq("client_id", data.clientId);
    if (archivedView === "active") assignQuery = assignQuery.is("archived_at", null);
    if (archivedView === "archived") assignQuery = assignQuery.not("archived_at", "is", null);

    const { data: assigns, error: aErr } = await assignQuery;
    if (aErr) throw new Error(aErr.message);

    const assignments = (assigns ?? []).filter((row: any) => {
      const t = row.questionnaire_templates;
      if (!t) return false;
      if (data.type && t.type !== data.type) return false;
      return true;
    });


    if (assignments.length === 0) return { rows: [] };

    const templateIds = Array.from(new Set(assignments.map((r: any) => r.template_id)));
    const clientIds = Array.from(new Set(assignments.map((r: any) => r.client_id)));

    const [{ data: questions, error: qErr }, { data: responses, error: rErr }] =
      await Promise.all([
        context.supabase
          .from("questionnaire_questions")
          .select("id, template_id, required, field_type, config, condition")
          .in("template_id", templateIds),
        context.supabase
          .from("questionnaire_responses")
          .select("id, template_id, client_id, status, started_at, completed_at, updated_at, reviewed_at")
          .in("template_id", templateIds)
          .in("client_id", clientIds),
      ]);
    if (qErr) throw new Error(qErr.message);
    if (rErr) throw new Error(rErr.message);

    const responseIds = ((responses ?? []) as any[]).map((r) => r.id);

    const answersByResp = new Map<string, Record<string, AnswerLike>>();
    if (responseIds.length > 0) {
      const { data: answers, error: ansErr } = await context.supabase
        .from("questionnaire_answers")
        .select(`response_id, ${ANSWER_COLUMNS}`)
        .in("response_id", responseIds);
      if (ansErr) throw new Error(ansErr.message);
      for (const a of (answers ?? []) as any[]) {
        const map = answersByResp.get(a.response_id) ?? {};
        map[a.question_id] = a as AnswerLike;
        answersByResp.set(a.response_id, map);
      }
    }

    const respByKey = new Map<string, any>();
    for (const r of (responses ?? []) as any[]) {
      respByKey.set(`${r.client_id}:${r.template_id}`, r);
    }

    const questionsByTpl = new Map<string, QuestionLike[]>();
    for (const q of (questions ?? []) as any[]) {
      const list = questionsByTpl.get(q.template_id) ?? [];
      list.push({
        id: q.id,
        required: q.required,
        field_type: q.field_type,
        config: parseConfig(q.config),
        condition: (q.condition ?? null) as QCondition | null,
      });
      questionsByTpl.set(q.template_id, list);
    }

    const searchNorm = (data.search ?? "").trim().toLowerCase();

    const rows: OverviewRow[] = assignments.map((row: any) => {
      const client = row.clients ?? {};
      const template = row.questionnaire_templates ?? {};
      const resp = respByKey.get(`${row.client_id}:${row.template_id}`);
      const qs = questionsByTpl.get(row.template_id) ?? [];
      const byQ = resp ? answersByResp.get(resp.id) ?? {} : {};
      // Zentrale Berechnung (Ticket K8): identischer Wert wie im Cockpit.
      const stats = computeProgress(qs, byQ);

      return {
        responseId: resp?.id ?? null,
        assignmentId: row.id,
        clientId: row.client_id,
        clientName: client.organization_name ?? "—",
        clientNumber: client.moco_client_number ?? null,
        templateId: row.template_id,
        templateTitle: template.title ?? "—",
        templateType: template.type,
        templateVariant: template.variant ?? null,
        status: (resp?.status as QStatus) ?? "not_started",
        lastActivity: resp?.updated_at ?? resp?.completed_at ?? resp?.started_at ?? null,
        completedAt: resp?.completed_at ?? null,
        startedAt: resp?.started_at ?? null,
        answeredRequired: stats.answeredRequired,
        totalRequired: stats.totalRequired,
        answeredQuestions: stats.answeredVisible,
        totalQuestions: stats.totalVisible,
        progress: stats.progress,
        reviewedAt: resp?.reviewed_at ?? null,
        archivedAt: row.archived_at ?? null,

      };
    });

    const filtered = rows.filter((r) => {
      if (data.status && r.status !== data.status) return false;
      if (searchNorm) {
        const hay = `${r.clientName} ${r.clientNumber ?? ""} ${r.templateTitle}`.toLowerCase();
        if (!hay.includes(searchNorm)) return false;
      }
      return true;
    });

    filtered.sort((a, b) => {
      const ta = a.lastActivity ? new Date(a.lastActivity).getTime() : 0;
      const tb = b.lastActivity ? new Date(b.lastActivity).getTime() : 0;
      return tb - ta;
    });

    return { rows: filtered };
  });

// ============================================================
// DETAIL
// ============================================================

export type AdminQuestionnaireDetail = {
  response: {
    id: string;
    status: QStatus;
    startedAt: string | null;
    completedAt: string | null;
    createdAt: string;
    updatedAt: string;
    lastEditorEmail: string | null;
    lastEditorName: string | null;
    /** Ticket AD4a: Zeitpunkt der Nachbearbeitung, `null` = offen. */
    reviewedAt: string | null;
    /** Zuweisung, an der der Archivstand hängt (`null` = keine Zuweisung mehr). */
    assignmentId: string | null;
    /** Interner Archivstand, `null` = aktiv. */
    archivedAt: string | null;
  };


  client: { id: string; name: string; clientNumber: string | null };
  template: {
    id: string;
    title: string;
    description: string | null;
    type: QTemplate["type"];
    variant: QTemplate["variant"];
    completionText?: string | null;
  };
  sections: Array<{
    id: string;
    title: string;
    introText: string | null;
    sortOrder: number;
  }>;
  questions: Array<{
    id: string;
    label: string;
    helpText: string | null;
    required: boolean;
    fieldType: QFieldType;
    options: Array<{ label: string; value: string }> | null;
    sortOrder: number;
    sectionId: string | null;
    config: QConfig | null;
    condition: QCondition | null;
    visible: boolean;
    /** Technischer Feldschlüssel für die Übergabe an externe Systeme. */
    apiKey: string | null;
  }>;
  answers: Array<{
    id: string;
    questionId: string;
    valueText: string | null;
    valueBool: boolean | null;
    valueDate: string | null;
    valueSelected: string[] | null;
    valueNumber: number | null;
    valueMatrix: Record<string, number> | null;
    valuePanels: Record<string, { value?: number | null; comment?: string | null }> | null;
    valueOtherText: string | null;
    valueUnknown: boolean | null;
    filePath: string | null;
    valueFiles: Array<{ path: string; name: string; size?: number | null; type?: string | null }> | null;
    updatedAt: string;
  }>;
  consent: {
    consentedAt: string;
    consentVersion: string;
    consentText: string;
  } | null;
  /** Fragebogenspezifische Einwilligungen mit Metadaten. */
  responseConsents: Array<{
    consentText: string;
    consentVersion: string;
    required: boolean;
    acceptedAt: string;
    channel: string;
  }>;
};

/** Fragebogenspezifische Einwilligungen einer Antwort laden. */
async function fetchResponseConsents(
  ctx: Ctx,
  responseId: string,
): Promise<AdminQuestionnaireDetail["responseConsents"]> {
  const { data } = await ctx.supabase
    .from("questionnaire_response_consents")
    .select("consent_text, consent_version, required, accepted_at, channel")
    .eq("response_id", responseId)
    .order("accepted_at", { ascending: true });
  return ((data ?? []) as any[]).map((r) => ({
    consentText: r.consent_text,
    consentVersion: r.consent_version,
    required: !!r.required,
    acceptedAt: r.accepted_at,
    channel: r.channel ?? "cockpit_creategy",
  }));
}

function mapAnswers(rows: any[]): AdminQuestionnaireDetail["answers"] {
  return rows.map((a: any) => ({
    id: a.id,
    questionId: a.question_id,
    valueText: a.value_text,
    valueBool: a.value_bool,
    valueDate: a.value_date,
    valueSelected: a.value_selected,
    valueNumber: a.value_number ?? null,
    valueMatrix: (a.value_matrix ?? null) as Record<string, number> | null,
    valuePanels: (a.value_panels ?? null) as AdminQuestionnaireDetail["answers"][number]["valuePanels"],
    valueOtherText: a.value_other_text ?? null,
    valueUnknown: a.value_unknown ?? null,
    filePath: a.file_path,
    valueFiles: (Array.isArray(a.value_files) ? a.value_files : null) as
      | Array<{ path: string; name: string; size?: number | null; type?: string | null }>
      | null,
    updatedAt: a.updated_at,
  }));
}

function mapQuestions(
  rows: any[],
  answerRows: any[],
  variant?: string | null,
): AdminQuestionnaireDetail["questions"] {
  const answers: Record<string, AnswerLike> = {};
  for (const a of answerRows) answers[a.question_id] = a as AnswerLike;
  const likes: QuestionLike[] = rows.map((q: any) => ({
    id: q.id,
    field_type: q.field_type,
    required: q.required,
    config: parseConfig(q.config),
    condition: (q.condition ?? null) as QCondition | null,
  }));
  return rows.map((q: any, i: number) => ({
    id: q.id,
    label: applyPlaceholders(q.label, variant),
    helpText: q.help_text ? applyPlaceholders(q.help_text, variant) : null,
    required: q.required,
    fieldType: q.field_type,
    options: q.options,
    sortOrder: q.sort_order,
    sectionId: q.section_id ?? null,
    config: parseConfig(q.config),
    condition: (q.condition ?? null) as QCondition | null,
    visible: isQuestionVisible(likes[i], likes, answers),
    apiKey: (q.api_key ?? null) as string | null,
  }));
}

const QUESTION_COLUMNS =
  "id, label, help_text, required, field_type, options, sort_order, section_id, config, condition, api_key";


export const getAdminQuestionnaireDetail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ responseId: z.string().uuid(), logView: z.boolean().optional() }).parse(input),
  )
  .handler(async ({ context, data }): Promise<AdminQuestionnaireDetail> => {
    await assertStaff(context);

    const { data: resp, error: rErr } = await context.supabase
      .from("questionnaire_responses")
      .select(
        "id, client_id, template_id, status, started_at, completed_at, created_at, updated_at, last_editor_user_id, reviewed_at",
      )
      .eq("id", data.responseId)
      .maybeSingle();
    if (rErr) throw new Error(rErr.message);
    if (!resp) throw new Error("Antwort nicht gefunden.");

    // Archivstand hängt an der Zuweisung, nicht am Rücklauf selbst.
    const { data: assignRow } = await context.supabase
      .from("client_questionnaire_assignments")
      .select("id, archived_at")
      .eq("client_id", resp.client_id)
      .eq("template_id", resp.template_id)
      .maybeSingle();



    const [
      { data: client, error: cErr },
      { data: template, error: tErr },
      { data: questions, error: qErr },
      { data: answers, error: aErr },
    ] = await Promise.all([
      context.supabase
        .from("clients")
        .select("id, organization_name, moco_client_number")
        .eq("id", resp.client_id)
        .maybeSingle(),
      context.supabase
        .from("questionnaire_templates")
        .select("id, title, description, type, variant, completion_text")
        .eq("id", resp.template_id)
        .maybeSingle(),
      context.supabase
        .from("questionnaire_questions")
        .select(QUESTION_COLUMNS)
        .eq("template_id", resp.template_id)
        .order("sort_order", { ascending: true }),
      context.supabase
        .from("questionnaire_answers")
        .select(ANSWER_COLUMNS)
        .eq("response_id", resp.id),
    ]);
    const { data: sectionRows } = await context.supabase
      .from("questionnaire_sections")
      .select("id, title, intro_text, sort_order")
      .eq("template_id", resp.template_id)
      .order("sort_order", { ascending: true });

    if (cErr) throw new Error(cErr.message);
    if (tErr) throw new Error(tErr.message);
    if (qErr) throw new Error(qErr.message);
    if (aErr) throw new Error(aErr.message);
    if (!template) throw new Error("Fragebogen nicht gefunden.");
    if (!client) throw new Error("Kunde nicht gefunden.");

    // Letzter Bearbeiter (optional)
    let editorEmail: string | null = null;
    let editorName: string | null = null;
    if (resp.last_editor_user_id) {
      // Privilegierter Serverzugang: die Rollenprüfung oben ist bereits
      // erfolgt, Redaktionskonten brauchen dafür keinen Vollzugriff auf die
      // Personalprofile.
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: prof } = await supabaseAdmin
        .from("profiles")
        .select("email, full_name, first_name, last_name")
        .eq("id", resp.last_editor_user_id)
        .maybeSingle();

      if (prof) {
        editorEmail = prof.email ?? null;
        editorName = prof.full_name || [prof.first_name, prof.last_name].filter(Boolean).join(" ") || null;
      }
      if (!editorEmail) {
        const { data: cust } = await context.supabase
          .from("customer_profiles")
          .select("email, first_name, last_name")
          .eq("user_id", resp.last_editor_user_id)
          .maybeSingle();
        if (cust) {
          editorEmail = cust.email ?? null;
          editorName = [cust.first_name, cust.last_name].filter(Boolean).join(" ") || null;
        }
      }
    }

    // Einwilligung: die neueste Einwilligung eines Kundennutzers, der jemals eine Antwort für diese Response bearbeitet hat.
    let consent: AdminQuestionnaireDetail["consent"] = null;
    if (resp.last_editor_user_id) {
      const { data: consentRow } = await context.supabase
        .from("questionnaire_consents")
        .select("consented_at, consent_version, consent_text")
        .eq("user_id", resp.last_editor_user_id)
        .maybeSingle();
      if (consentRow) {
        consent = {
          consentedAt: consentRow.consented_at,
          consentVersion: consentRow.consent_version,
          consentText: consentRow.consent_text,
        };
      }
    }

    if (data.logView) {
      await logAccess(context, "QUESTIONNAIRE_VIEW", resp.id, {
        client_id: resp.client_id,
        template_id: resp.template_id,
        template_title: template.title,
      });
    }

    return {
      response: {
        id: resp.id,
        status: resp.status,
        startedAt: resp.started_at,
        completedAt: resp.completed_at,
        createdAt: resp.created_at,
        updatedAt: resp.updated_at,

        lastEditorEmail: editorEmail,
        lastEditorName: editorName,
        reviewedAt: (resp as any).reviewed_at ?? null,
        assignmentId: (assignRow as any)?.id ?? null,
        archivedAt: (assignRow as any)?.archived_at ?? null,

      },
      client: {
        id: client.id,
        name: client.organization_name,
        clientNumber: client.moco_client_number ?? null,
      },
      template: {
        id: template.id,
        title: template.title,
        description: template.description,
        type: template.type,
        variant: template.variant,
        completionText: (template as any).completion_text ?? null,
      },
      sections: ((sectionRows ?? []) as any[]).map((s) => ({
        id: s.id,
        title: applyPlaceholders(s.title, template.variant),
        introText: s.intro_text ? applyPlaceholders(s.intro_text, template.variant) : null,
        sortOrder: s.sort_order,
      })),

      questions: mapQuestions((questions ?? []) as any[], (answers ?? []) as any[], template.variant),
      answers: mapAnswers((answers ?? []) as any[]),
      consent,
      responseConsents: await fetchResponseConsents(context, resp.id),
    };
  });


// ============================================================
// DATEI-DOWNLOAD
// ============================================================

export const getAdminAnswerFileSignedUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ answerId: z.string().uuid() }).parse(input))
  .handler(async ({ context, data }) => {
    await assertStaff(context);

    const { data: answer, error } = await context.supabase
      .from("questionnaire_answers")
      .select("id, response_id, question_id, file_path")
      .eq("id", data.answerId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!answer || !answer.file_path) throw new Error("Datei nicht gefunden.");

    const filename = answer.file_path.split("/").pop() ?? "download";
    const { data: signed, error: sErr } = await context.supabase.storage
      .from("questionnaire-uploads")
      .createSignedUrl(answer.file_path, 60 * 5, { download: filename });
    if (sErr || !signed?.signedUrl) {
      throw new Error(sErr?.message ?? "Signierte URL konnte nicht erzeugt werden.");
    }

    await logAccess(context, "QUESTIONNAIRE_FILE_ACCESS", answer.response_id, {
      answer_id: answer.id,
      question_id: answer.question_id,
      file_path: answer.file_path,
    });

    return { url: signed.signedUrl, filename };
  });

export const getAdminAnswerFileSignedUrlByPath = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({ answerId: z.string().uuid(), path: z.string().trim().min(1).max(500) })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context);

    const { data: answer, error } = await context.supabase
      .from("questionnaire_answers")
      .select("id, response_id, question_id, value_files")
      .eq("id", data.answerId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    const list = Array.isArray(answer?.value_files) ? (answer!.value_files as any[]) : [];
    const entry = list.find((f) => f && f.path === data.path);
    if (!entry) throw new Error("Datei nicht gefunden.");

    const filename = String(entry.name ?? entry.path.split("/").pop() ?? "download");
    const { data: signed, error: sErr } = await context.supabase.storage
      .from("questionnaire-uploads")
      .createSignedUrl(entry.path, 60 * 5, { download: filename });
    if (sErr || !signed?.signedUrl) {
      throw new Error(sErr?.message ?? "Signierte URL konnte nicht erzeugt werden.");
    }

    await logAccess(context, "QUESTIONNAIRE_FILE_ACCESS", answer!.response_id, {
      answer_id: answer!.id,
      question_id: answer!.question_id,
      file_path: entry.path,
    });

    return { url: signed.signedUrl, filename };
  });

// ============================================================
// EXPORT: CSV
// ============================================================

const dateFmt = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function csvEscape(v: string): string {
  const s = v ?? "";
  if (/[";\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function statusLabel(s: QStatus): string {
  return s === "completed" ? "Abgeschlossen" : s === "in_progress" ? "In Bearbeitung" : "Nicht begonnen";
}

function fieldTypeLabel(t: QFieldType): string {
  return FIELD_TYPE_LABELS[t as string] ?? String(t);
}

export function formatAnswerPlain(
  field: QFieldType,
  answer: AdminQuestionnaireDetail["answers"][number] | undefined,
  options: Array<{ label: string; value: string }> | null,
  config?: QConfig | null,
): string {
  if (!answer) return "";
  const cfg = config ?? {};
  switch (field as string) {
    case "text_short":
    case "text_long":
    case "email":
      return answer.valueText ?? "";
    case "checkbox":
      return answer.valueBool ? "Ja" : "Nein";
    case "date":
      if (answer.valueUnknown) return cfg.unknown_label ?? "noch nicht bekannt";
      if (!answer.valueDate) return "";
      try {
        return new Date(answer.valueDate).toLocaleDateString("de-DE");
      } catch {
        return answer.valueDate;
      }
    case "select":
    case "radio": {
      const sel = answer.valueSelected ?? [];
      if (sel.length === 0) return "";
      const labels = sel.map((v) =>
        v === OTHER_VALUE
          ? `${cfg.other_label ?? "Sonstiges"}: ${answer.valueOtherText ?? ""}`.trim()
          : (options?.find((o) => o.value === v)?.label ?? v),
      );
      return labels.join(", ");
    }
    case "scale": {
      if (answer.valueNumber == null) return answer.valueText ?? "";
      const base = `${answer.valueNumber}/5`;
      return answer.valueText ? `${base} — ${answer.valueText}` : base;
    }
    case "number": {
      if (answer.valueNumber == null) return "";
      const base = formatEuroAmount(answer.valueNumber, cfg.number_unit ?? "€");
      return cfg.number_estimated ? `${base} (geschätzt)` : base;
    }
    case "matrix": {
      const m = answer.valueMatrix ?? {};
      const rows = matrixRows(cfg);
      const steps = matrixSteps(cfg);
      const max = steps[steps.length - 1] ?? 5;
      const parts = rows
        .filter((r) => m[r.key] != null)
        .map((r) => {
          const poles =
            r.left_label || r.right_label
              ? ` (${r.left_label ?? ""} ↔ ${r.right_label ?? ""})`
              : "";
          return `${r.label}${poles}: ${m[r.key]}/${max}`;
        });
      return parts.join(" | ");
    }
    case "image_priority": {
      const ranked = answer.valueSelected ?? [];
      if (ranked.length === 0) return "";
      return ranked
        .map((v, i) => `${i + 1}. ${options?.find((o) => o.value === v)?.label ?? v}`)
        .join(" | ");
    }
    case "image_rating": {
      const panels = answer.valuePanels ?? {};
      const parts = (options ?? [])
        .filter((o) => panels[o.value]?.value != null || panels[o.value]?.comment)
        .map((o) => {
          const p = panels[o.value] ?? {};
          const val = p.value == null ? "—" : `${p.value}/5`;
          return p.comment ? `${o.label}: ${val} — ${p.comment}` : `${o.label}: ${val}`;
        });
      return parts.join(" | ");
    }
    // Ticket D3: Kombination aus Palette und Logo, gelesen aus der
    // Momentaufnahme des Auswahlzeitpunkts.
    case "campaign_design":
      return formatDesignAnswer(parseDesignSnapshot(answer.valueText));
    case "file":
      {
        const list = Array.isArray(answer.valueFiles) ? answer.valueFiles : [];
        if (list.length > 0) return list.map((f: any) => f?.name ?? f?.path).join(", ");
        return answer.filePath ? (answer.filePath.split("/").pop() ?? "Datei") : "";
      }
    default:
      return "";
  }
}


export const exportQuestionnaireCsv = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ responseId: z.string().uuid() }).parse(input))
  .handler(async ({ context, data }): Promise<{ filename: string; content: string }> => {
    await assertStaff(context);

    const detail = await getDetailInternal(context, data.responseId);

    const meta: Array<[string, string]> = [
      ["Kunde", `${detail.client.name}${detail.client.clientNumber ? " (" + detail.client.clientNumber + ")" : ""}`],
      ["external_id", detail.client.id],
      ["Fragebogen", detail.template.title],
      ["Typ", detail.template.type],
      ["Variante", detail.template.variant ?? ""],
      ["Status", statusLabel(detail.response.status)],
      ["Gestartet", detail.response.startedAt ? dateFmt.format(new Date(detail.response.startedAt)) : ""],
      ["Abgeschlossen", detail.response.completedAt ? dateFmt.format(new Date(detail.response.completedAt)) : ""],
      ["created_at", detail.response.createdAt],
      ["updated_at", detail.response.updatedAt],
      ["locale", "de-DE"],
      ["source", "cockpit_creategy"],
      ["Letzte Aktivität", dateFmt.format(new Date(detail.response.updatedAt))],
      ["Letzter Bearbeiter", detail.response.lastEditorEmail ?? ""],
      [
        "Einwilligung",
        detail.consent
          ? `${dateFmt.format(new Date(detail.consent.consentedAt))} (Version ${detail.consent.consentVersion})`
          : "—",
      ],
      ["Exportiert am", dateFmt.format(new Date())],
    ];


    const lines: string[] = [];
    for (const [k, v] of meta) lines.push(`${csvEscape(k)};${csvEscape(v)}`);
    lines.push("");
    lines.push(
      ["Abschnitt", "Reihenfolge", "Feldschlüssel", "Frage", "Feldtyp", "Pflicht", "Antwort"]
        .map(csvEscape)
        .join(";"),
    );

    const sectionTitle = new Map(detail.sections.map((s) => [s.id, s.title]));
    const ansByQ = new Map(detail.answers.map((a) => [a.questionId, a]));
    for (const q of detail.questions) {
      // Bedingte, nicht sichtbare Fragen entfallen im Export.
      if (!q.visible) continue;
      const value = formatAnswerPlain(q.fieldType, ansByQ.get(q.id), q.options, q.config);
      lines.push(
        [
          (q.sectionId ? sectionTitle.get(q.sectionId) : "") ?? "",
          String(q.sortOrder + 1),
          q.apiKey ?? "",
          q.label,
          fieldTypeLabel(q.fieldType),
          q.required ? "Ja" : "Nein",
          value,
        ]
          .map(csvEscape)
          .join(";"),
      );
    }


    if (detail.responseConsents.length > 0) {
      lines.push("");
      lines.push(
        ["Einwilligung", "Version", "Pflicht", "Erteilt am", "Kanal"].map(csvEscape).join(";"),
      );
      for (const c of detail.responseConsents) {
        lines.push(
          [
            c.consentText,
            c.consentVersion,
            c.required ? "Ja" : "Nein",
            dateFmt.format(new Date(c.acceptedAt)),
            c.channel,
          ]
            .map(csvEscape)
            .join(";"),
        );
      }
    }

    const bom = "\uFEFF";
    const content = bom + lines.join("\r\n");
    const filename = safeFilename(
      `Fragebogen_${detail.template.title}_${detail.client.name}.csv`,
    );

    await logAccess(context, "QUESTIONNAIRE_EXPORT_CSV", detail.response.id, {
      client_id: detail.client.id,
      template_id: detail.template.id,
    });

    return { filename, content };
  });

// ============================================================
// EXPORT: PDF
// ============================================================

export const exportQuestionnairePdf = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ responseId: z.string().uuid() }).parse(input))
  .handler(async ({ context, data }): Promise<{ filename: string; base64: string }> => {
    await assertStaff(context);

    const detail = await getDetailInternal(context, data.responseId);

    let actorEmail: string | null = null;
    try {
      const { data: p } = await context.supabase
        .from("profiles")
        .select("email, full_name")
        .eq("id", context.userId)
        .maybeSingle();
      actorEmail = p?.email ?? p?.full_name ?? null;
    } catch {
      /* ignore */
    }

    const { renderQuestionnairePdf } = await import("./questionnaire-admin-pdf.server");
    const pdfBytes = await renderQuestionnairePdf({
      detail,
      actorEmail,
      exportedAt: new Date(),
      supabase: context.supabase,
    });

    // Uint8Array → base64
    let binary = "";
    for (let i = 0; i < pdfBytes.length; i++) binary += String.fromCharCode(pdfBytes[i]);
    const base64 = btoa(binary);

    const filename = safeFilename(
      `Fragebogen_${detail.template.title}_${detail.client.name}.pdf`,
    );

    await logAccess(context, "QUESTIONNAIRE_EXPORT_PDF", detail.response.id, {
      client_id: detail.client.id,
      template_id: detail.template.id,
    });

    return { filename, base64 };
  });

// ============================================================
// interne Helper
// ============================================================

function safeFilename(name: string): string {
  return name
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, "_")
    .slice(0, 180);
}

export async function getDetailInternal(ctx: Ctx, responseId: string): Promise<AdminQuestionnaireDetail> {
  const { data: resp, error: rErr } = await ctx.supabase
    .from("questionnaire_responses")
    .select("id, client_id, template_id, status, started_at, completed_at, created_at, updated_at, last_editor_user_id, reviewed_at")
    .eq("id", responseId)
    .maybeSingle();
  if (rErr) throw new Error(rErr.message);
  if (!resp) throw new Error("Antwort nicht gefunden.");

  // Archivstand hängt an der Zuweisung, nicht am Rücklauf selbst.
  const { data: assignRow } = await ctx.supabase
    .from("client_questionnaire_assignments")
    .select("id, archived_at")
    .eq("client_id", resp.client_id)
    .eq("template_id", resp.template_id)
    .maybeSingle();



  const { data: sectionRows } = await ctx.supabase
    .from("questionnaire_sections")
    .select("id, title, intro_text, sort_order")
    .eq("template_id", resp.template_id)
    .order("sort_order", { ascending: true });

  const [
    { data: client },
    { data: template, error: tErr },
    { data: questions, error: qErr },
    { data: answers, error: aErr },
  ] = await Promise.all([
    ctx.supabase.from("clients").select("id, organization_name, moco_client_number").eq("id", resp.client_id).maybeSingle(),

    ctx.supabase.from("questionnaire_templates").select("id, title, description, type, variant, completion_text").eq("id", resp.template_id).maybeSingle(),
    ctx.supabase
      .from("questionnaire_questions")
      .select(QUESTION_COLUMNS)
      .eq("template_id", resp.template_id)
      .order("sort_order", { ascending: true }),
    ctx.supabase
      .from("questionnaire_answers")
      .select(ANSWER_COLUMNS)
      .eq("response_id", resp.id),

  ]);
  if (tErr) throw new Error(tErr.message);
  if (qErr) throw new Error(qErr.message);
  if (aErr) throw new Error(aErr.message);
  if (!template) throw new Error("Fragebogen nicht gefunden.");

  let editorEmail: string | null = null;
  let editorName: string | null = null;
  if (resp.last_editor_user_id) {
    const { data: cust } = await ctx.supabase
      .from("customer_profiles")
      .select("email, first_name, last_name")
      .eq("user_id", resp.last_editor_user_id)
      .maybeSingle();
    if (cust) {
      editorEmail = cust.email ?? null;
      editorName = [cust.first_name, cust.last_name].filter(Boolean).join(" ") || null;
    }
  }

  let consent: AdminQuestionnaireDetail["consent"] = null;
  if (resp.last_editor_user_id) {
    const { data: consentRow } = await ctx.supabase
      .from("questionnaire_consents")
      .select("consented_at, consent_version, consent_text")
      .eq("user_id", resp.last_editor_user_id)
      .maybeSingle();
    if (consentRow) {
      consent = {
        consentedAt: consentRow.consented_at,
        consentVersion: consentRow.consent_version,
        consentText: consentRow.consent_text,
      };
    }
  }

  return {
    response: {
      id: resp.id,
      status: resp.status,
      startedAt: resp.started_at,
      completedAt: resp.completed_at,
      createdAt: resp.created_at,
      updatedAt: resp.updated_at,
      reviewedAt: (resp as any).reviewed_at ?? null,
      assignmentId: (assignRow as any)?.id ?? null,
      archivedAt: (assignRow as any)?.archived_at ?? null,

      lastEditorEmail: editorEmail,
      lastEditorName: editorName,
    },
    client: {
      id: client?.id ?? resp.client_id,
      name: client?.organization_name ?? "—",
      clientNumber: client?.moco_client_number ?? null,
    },
    template: {
      id: template.id,
      title: template.title,
      description: template.description,
      type: template.type,
      variant: template.variant,
      completionText: (template as any).completion_text ?? null,
    },
    sections: ((sectionRows ?? []) as any[]).map((s) => ({
      id: s.id,
      title: applyPlaceholders(s.title, template.variant),
      introText: s.intro_text ? applyPlaceholders(s.intro_text, template.variant) : null,
      sortOrder: s.sort_order,
    })),

    questions: mapQuestions((questions ?? []) as any[], (answers ?? []) as any[], template.variant),
    answers: mapAnswers((answers ?? []) as any[]),
    consent,
    responseConsents: await fetchResponseConsents(ctx, resp.id),
  };
}

export { statusLabel, fieldTypeLabel };

/**
 * Fragebogen-Rückläufer intern archivieren oder zurückholen.
 *
 * Der Archivstand hängt an der Zuweisung, damit auch Einträge ohne Rücklauf
 * („Nicht begonnen") archiviert werden können. Das Kundencockpit wertet das
 * Feld bewusst NICHT aus – für Kundinnen und Kunden ändert sich nichts.
 */
export const setQuestionnaireArchived = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        assignmentIds: z.array(z.string().uuid()).min(1).max(500),
        archived: z.boolean(),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context);

    const patch = data.archived
      ? { archived_at: new Date().toISOString(), archived_by: context.userId }
      : { archived_at: null, archived_by: null };

    const { error } = await context.supabase
      .from("client_questionnaire_assignments")
      .update(patch)
      .in("id", data.assignmentIds);
    if (error) throw new Error(error.message);

    for (const id of data.assignmentIds) {
      try {
        await (await import("@/lib/audit.server")).writeAuditEvent({
          action: data.archived ? "QUESTIONNAIRE_ARCHIVE" : "QUESTIONNAIRE_UNARCHIVE",
          tableName: "client_questionnaire_assignments",
          recordId: id,
          newData: patch,
          context: "admin_questionnaire",
          actorId: context.userId,
        });
      } catch (err) {
        console.error("[audit] questionnaire archive log failed", err);
      }
    }

    return { ok: true, count: data.assignmentIds.length };
  });

/**
 * Ticket D4: Speichern der Fragebogen-Zuordnungen eines Kunden.
 *
 * Bisher schrieb die Backend-Ansicht direkt in die Tabelle – damit gab es keine
 * Stelle, an der eine Benachrichtigung hängen konnte. Ab hier läuft jede
 * Änderung über diese Funktion: Zuordnungen anlegen, abgewählte entfernen und
 * anschließend für ein neu zugeordnetes Standard-Kampagnendesign die
 * Aufforderung an alle Zugänge des Kunden versenden.
 */
export const saveClientQuestionnaireAssignments = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        clientId: z.string().uuid(),
        addTemplateIds: z.array(z.string().uuid()).max(100).default([]),
        removeTemplateIds: z.array(z.string().uuid()).max(100).default([]),
        origin: z.string().url().optional(),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context);

    if (data.removeTemplateIds.length > 0) {
      const { error } = await context.supabase
        .from("client_questionnaire_assignments")
        .delete()
        .eq("client_id", data.clientId)
        .in("template_id", data.removeTemplateIds);
      if (error) throw new Error(error.message);
    }

    if (data.addTemplateIds.length > 0) {
      const { error } = await context.supabase
        .from("client_questionnaire_assignments")
        .insert(
          data.addTemplateIds.map((template_id) => ({ template_id, client_id: data.clientId })),
        );
      if (error) throw new Error(error.message);
    }

    try {
      await (await import("@/lib/audit.server")).writeAuditEvent({
        action: "QUESTIONNAIRE_ASSIGNMENTS_SAVED",
        tableName: "client_questionnaire_assignments",
        recordId: data.clientId,
        newData: { added: data.addTemplateIds, removed: data.removeTemplateIds },
        context: "admin_client_questionnaires",
        actorId: context.userId,
      });
    } catch (err) {
      console.error("[audit] questionnaire assignment log failed", err);
    }

    // Benachrichtigung: nur neu zugeordnetes Standard-Kampagnendesign.
    let notified = 0;
    let notifyError: string | null = null;
    if (data.addTemplateIds.length > 0) {
      try {
        const { data: tpls } = await context.supabase
          .from("questionnaire_templates")
          .select("id, type, slug, title")
          .in("id", data.addTemplateIds);
        const { designTemplatesToNotify } = await import("@/lib/campaign-design-notify");
        const targets = designTemplatesToNotify(data.addTemplateIds, (tpls ?? []) as any[]);
        if (targets.length > 0) {
          const [{ supabaseAdmin }, { sendDesignAssignmentNotice }] = await Promise.all([
            import("@/integrations/supabase/client.server"),
            import("@/lib/campaign-design-notify.server"),
          ]);
          for (const template of targets) {
            const result = await sendDesignAssignmentNotice(supabaseAdmin as any, {
              clientId: data.clientId,
              template,
              ...(data.origin ? { origin: data.origin } : {}),
            });
            notified += result.queued;
            if (result.queued === 0) {
              notifyError =
                result.attempted === 0
                  ? "Dieser Kunde hat keinen Zugang zum Kundenbereich – es wurde niemand aufgefordert."
                  : "Die Aufforderung zum Kampagnendesign konnte nicht versendet werden.";
            }
          }
        }
      } catch (err) {
        notifyError = err instanceof Error ? err.message : "Unbekannter Fehler beim Versand.";
        console.error("[kampagnendesign-notify] Aufforderung fehlgeschlagen", err);
      }
    }

    return {
      ok: true,
      added: data.addTemplateIds.length,
      removed: data.removeTemplateIds.length,
      notified,
      notifyError,
    };
  });

